import React from 'react';
import { Typography, Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export interface PageHeaderProps {
  title: string;
  description?: string;
  mobileDescription?: string;
  action?: React.ReactNode;
}

/**
 * Consistent page header component for all pages.
 * Provides clear information hierarchy and responsive behavior.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  mobileDescription,
  action,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        mb: isMobile ? 2 : 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1.5 : 2,
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          sx={{
            fontWeight: 600,
            mb: description || mobileDescription ? (isMobile ? 0.75 : 1) : 0,
          }}
        >
          {title}
        </Typography>
        {(description || mobileDescription) && (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontSize: isMobile ? '0.875rem' : '1rem',
              lineHeight: 1.6,
            }}
          >
            {isMobile && mobileDescription ? mobileDescription : description}
          </Typography>
        )}
      </Box>
      {action && (
        <Box sx={{ flexShrink: 0 }}>{action}</Box>
      )}
    </Box>
  );
};
